/**
 * bb-region-filter.js
 * ───────────────────────────────────────────────────────────────────────────
 * Client-side region filtering (spec §3). Shows/hides product cards by
 * membership of the visitor's resolved region in each card's region tags.
 *
 * Rules (launch scope = served regions A–C):
 *   - SERVED visitor (region ∈ A–C):
 *       · show IF region ∈ card.regions (membership → multi-region products work)
 *       · hide IF region ∉ card.regions; UNTAGGED card → hidden (fail-closed)
 *   - UNKNOWN visitor (region null — geo failed/denied/pending, theme editor) OR
 *     KNOWN-UNSERVED visitor (D–I / AK-HI / non-US) → show ALL, ignoring tags, so
 *     the store is never empty. The PDP (bb-availability.js) decides buy vs. the
 *     notify panel; the grid just lets everyone browse the full catalog.
 *
 * This is a UX layer, not enforcement: cards stay in the DOM (crawler-visible).
 * True purchase prevention would need a server-side checkout guardrail — out of
 * scope here (spec §0.3 / §10).
 *
 * Re-render aware: Horizon repaints the grid via infinite scroll, facet
 * section-morphs, and predictive search. A MutationObserver re-applies the
 * filter to cards added or repurposed after the initial pass, and a
 * `filter:update` listener re-sweeps after facet changes.
 */

import { getRegionTags, isServedRegion } from '@theme/bb-region-config';
import { getLocation } from '@theme/bb-location-store';
import { BB_LOCATION_EVENT, getLastResolved } from '@theme/bb-geo-detect';

const CARD_SELECTOR = 'product-card';
const ITEM_SELECTOR = '.product-grid__item';
const HIDDEN_CLASS = 'bb-region-hidden';
const FILTER_ATTR = 'data-bb-filtering';
/** Horizon dispatches this on `document` when collection facets change. */
const FILTER_UPDATE_EVENT = 'filter:update';

let _region = null;
let _resolved = false;
let _observer = null;
let _sweepQueued = false;

/** The element to hide: the grid item (so its cell collapses) or the card. */
function hostFor(card) {
  return card.closest(ITEM_SELECTOR) || card;
}

/**
 * Parsed region tags for a card, memoized on the element and invalidated when
 * its `data-regions` value changes (e.g. a morph repurposes the card). Avoids
 * re-parsing every card's tags on each full sweep.
 */
function cardTags(card) {
  const raw = card.getAttribute('data-regions') || '';
  if (card.__bbTagsRaw !== raw || !card.__bbTags) {
    card.__bbTagsRaw = raw;
    card.__bbTags = getRegionTags(raw);
  }
  return card.__bbTags;
}

/**
 * Membership rule:
 *   - SERVED visitor (region ∈ A–C) → show only cards tagged for the region
 *     (untagged fails closed)
 *   - UNKNOWN or KNOWN-UNSERVED visitor → show everything (browse all)
 */
function shouldShow(card) {
  if (!isServedRegion(_region)) return true;
  return cardTags(card).includes(_region);
}

function applyToCard(card) {
  hostFor(card).classList.toggle(HIDDEN_CLASS, !shouldShow(card));
}

function applyAll(root) {
  const scope = root && root.querySelectorAll ? root : document;
  for (const card of scope.querySelectorAll(CARD_SELECTOR)) applyToCard(card);
}

/** Reveal the grid once filtering has been applied (clears flash-mitigation). */
function reveal() {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute(FILTER_ATTR, 'ready');
  }
}

function handleResolution(detail) {
  _region = detail && detail.region != null ? detail.region : null;
  _resolved = true;
  applyAll(document);
  reveal();
}

/** Coalesce bursts of mutations / facet updates into one sweep per frame. */
function queueSweep() {
  if (_sweepQueued || !_resolved) return;
  _sweepQueued = true;
  requestAnimationFrame(() => {
    _sweepQueued = false;
    applyAll(document);
  });
}

function startObserver() {
  if (_observer || typeof MutationObserver === 'undefined' || !document.body) return;

  _observer = new MutationObserver((mutations) => {
    if (!_resolved) return; // pending CSS hides new cards; applyAll on resolve covers them

    const cards = new Set();
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches(CARD_SELECTOR)) cards.add(node);
          for (const inner of node.querySelectorAll(CARD_SELECTOR)) cards.add(inner);
        }
      } else if (mutation.type === 'attributes') {
        // A morph can repurpose an existing card for a different product.
        const target = mutation.target;
        if (target instanceof Element && target.matches(CARD_SELECTOR)) cards.add(target);
      }
    }
    for (const card of cards) applyToCard(card);
  });

  _observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-product-id', 'data-regions'],
  });
}

function boot() {
  if (typeof document === 'undefined') return;

  // Subscribe BEFORE reading the last value so a resolution firing in between
  // is never missed (re-applying is idempotent if both paths run).
  document.addEventListener(BB_LOCATION_EVENT, (event) => handleResolution(event.detail));
  document.addEventListener(FILTER_UPDATE_EVENT, queueSweep);
  startObserver();

  // PERF/LCP: reveal as early as possible. A returning visitor has a cached
  // location in localStorage, so apply + reveal synchronously here — this takes
  // every repeat visit off the ipapi round-trip entirely (no waiting on the
  // network to un-hide the grid). First visitors fall through to the detection
  // event (and the flash-guard safety timeout caps the worst case).
  //
  // Skip the cached path in the theme editor: detection emits an unmapped
  // (show-all) result in design mode, and applying a stale live-store cache here
  // would briefly geofence the editor preview before that event corrects it.
  const inDesignMode = typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode;
  if (!inDesignMode) {
    const cached = getLocation();
    if (cached) {
      handleResolution(cached);
      return;
    }
  }

  const last = getLastResolved();
  if (last !== undefined) handleResolution(last);
}

boot();
